import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { Ellipses } from '../../assets/icons'
import { POPOVER } from '../../constants'
import { getViewport } from '../../utils'
import Portal from '../Portal'
import { usePopupDismiss } from '../usePopupDismiss'

const Root = styled.div`
  display: inline-flex;
`

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  color: ${p => p.theme.primaryMedium ?? '#4fadcc'};

  &:hover,
  &[aria-expanded='true'] {
    color: ${p => p.theme.primaryColor};
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.primaryColor};
    outline-offset: 2px;
    border-radius: 4px;
  }

  &[aria-disabled='true'] {
    cursor: default;
  }
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid ${p => p.theme.primaryLight};
  border-top-color: ${p => p.theme.primaryColor};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const MENU_GAP = 10

// Rendered through Portal and pinned to the trigger's viewport rect rather than positioned
// against it, so the surrounding card cannot clip the menu or paint over it. Placement arrives as
// an inline style; until it is measured the menu is laid out but not shown.
const Menu = styled.ul`
  position: fixed;
  z-index: ${POPOVER};
  visibility: ${p => (p.$placed ? 'visible' : 'hidden')};
  margin: 0;
  padding: 10px 0;
  list-style: none;
  min-width: 100px;
  background: #ffffff;
  border: solid 1px #979797;
  border-radius: 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
`

const MenuItem = styled.button`
  display: block;
  width: 100%;
  height: 30px;
  padding: 0 20px;
  background: none;
  border: none;
  outline: none;
  text-align: left;
  white-space: nowrap;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  line-height: 30px;
  cursor: pointer;
  color: ${p => (p.$danger ? '#ce0b24' : p.theme.mediumGray)};

  &:hover,
  &:focus-visible {
    background: ${p => p.theme.primaryLight};
    outline: none;
  }
`

/**
 * Row action menu opened by an ellipsis button.
 * Renders nothing when `items` is empty.
 */
const ActionMenu = ({ items = [], ariaLabel = 'Actions', busy = false, busyLabel, className }) => {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState(null)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  // Escape and a chosen action hand focus back to the trigger, since focus moved into the menu to
  // open it. An outside click has already placed focus itself, so it is left where the user put it.
  const close = useCallback(reason => {
    setOpen(false)
    if (reason !== 'outside') {
      triggerRef.current?.focus()
    }
  }, [])

  usePopupDismiss([rootRef, menuRef], close, open)

  // Right edges stay aligned; the menu drops below the trigger unless it would run past the
  // viewport and there is more room above.
  const place = useCallback(() => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) return

    const { top: spaceAbove, bottom, right } = trigger.getBoundingClientRect()
    const { height: viewportHeight, width: viewportWidth } = getViewport()
    const spaceNeeded = menu.offsetHeight + MENU_GAP
    const spaceBelow = viewportHeight - bottom
    const opensUpward = spaceBelow < spaceNeeded && spaceAbove > spaceBelow

    setPlacement({
      right: viewportWidth - right,
      ...(opensUpward
        ? { bottom: viewportHeight - spaceAbove + MENU_GAP }
        : { top: bottom + MENU_GAP }),
    })
  }, [])

  // The menu is portalled out of the row, so its buttons are no longer what Tab reaches from the
  // trigger. Focus moves into the first action instead, which is what gives the keyboard a way in.
  // Placement is measured in a layout effect, so by now the menu is placed and visible.
  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector('button')?.focus()
  }, [open])

  // Measure before paint so the menu is never shown at the wrong end of the trigger. It is fixed
  // to the viewport, so anything that moves the trigger has to move it too.
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null)
      return undefined
    }

    place()

    // Capture, so scrolling any container the trigger sits in is caught, not just the page.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)

    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  // Tab runs off the end of the menu into whatever follows it in the portal, so close it rather
  // than leave it open over a page the user has already moved on from.
  const handleMenuBlur = e => {
    const next = e.relatedTarget
    if (next && (menuRef.current?.contains(next) || rootRef.current?.contains(next))) return
    setOpen(false)
  }

  if (items.length === 0) {
    return null
  }

  return (
    <Root ref={rootRef} className={className}>
      <Trigger
        ref={triggerRef}
        type='button'
        aria-expanded={open}
        aria-label={busy ? (busyLabel ?? ariaLabel) : ariaLabel}
        aria-busy={busy}
        aria-disabled={busy}
        onClick={() => {
          // Keep the trigger focusable while busy; aria-disabled does not block clicks.
          if (busy) return
          setOpen(wasOpen => !wasOpen)
        }}>
        {busy ? <Spinner /> : <Ellipses />}
      </Trigger>
      {open && (
        <Portal>
          <Menu
            ref={menuRef}
            style={placement ?? undefined}
            $placed={placement !== null}
            onBlur={handleMenuBlur}>
            {items.map(item => (
              <li key={item.label}>
                <MenuItem
                  type='button'
                  $danger={item.danger}
                  onClick={() => {
                    // Focus the trigger before the action runs; the action may unmount it.
                    close()
                    item.onClick()
                  }}>
                  {item.label}
                </MenuItem>
              </li>
            ))}
          </Menu>
        </Portal>
      )}
    </Root>
  )
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      danger: PropTypes.bool,
    })
  ),
  ariaLabel: PropTypes.string,
  busy: PropTypes.bool,
  busyLabel: PropTypes.string,
  className: PropTypes.string,
}

export default ActionMenu
